import { Tooltip } from 'antd';
import { useMemo } from 'react';
import { useRequest } from 'ahooks';
import { Long, UserRankLevelDTO, companyRankController } from '../api';
import { useCompany } from '../hooks/company';
import { RANK_LEVELS, defaultUserRankLevel } from '../utils/rankLevel';
import './userRankLevelMedal.scss';

const formatWan = (yuan?: number | string) => {
  return (Number(yuan || 0) / 10000).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * 批量拉取公司内用户的年度段位，返回按 userId 查询的 getter。
 * 接口未返回的用户（无年度放款）按黑铁兜底；接口异常时返回 undefined 静默隐藏勋章。
 */
export const useUserRankLevels = () => {
  const { id: companyId, config: { companyRankConfig } = {} } = useCompany();

  const { data } = useRequest(
    () => {
      return companyRankController.userLevels(companyId).catch(() => undefined);
    },
    {
      refreshDeps: [companyId],
    },
  );

  return useMemo(() => {
    const levelMap = new Map<string, UserRankLevelDTO>();
    data?.forEach((item) => {
      if (item.userId !== undefined) {
        levelMap.set(String(item.userId), item);
      }
    });
    const fallback = defaultUserRankLevel(companyRankConfig?.annualLevels);

    return (userId?: Long): UserRankLevelDTO | undefined => {
      // 接口失败或未返回时不兜底，避免全员展示黑铁造成误导
      if (!data || userId === undefined || userId === null) {
        return undefined;
      }
      return levelMap.get(String(userId)) || fallback;
    };
  }, [data, companyRankConfig]);
};

/**
 * 年度段位勋章，悬停展示当前段位、年度业绩与距下一段位的差额
 */
export default ({
  level,
  className,
}: {
  level?: UserRankLevelDTO;
  className?: string;
}) => {
  if (!level?.level) {
    return null;
  }

  const medal = RANK_LEVELS[level.level - 1];
  if (!medal) {
    return null;
  }

  const tooltip = (
    <div className="annual-rank-level-tooltip">
      <div>
        当前年度段位：<strong>{level.levelName}</strong>
      </div>
      <div>年度业绩：{formatWan(level.annualBrokerage)} 万</div>
      {level.nextLevelName ? (
        <div>
          距 <strong>{level.nextLevelName}</strong> 还差{' '}
          {formatWan(level.gapBrokerage)} 万
        </div>
      ) : (
        <div>已是最高段位，保持王者风范！</div>
      )}
    </div>
  );

  return (
    <Tooltip title={tooltip}>
      <img
        className={['user-rank-level-medal', className]
          .filter(Boolean)
          .join(' ')}
        src={medal.icon}
        alt={`年度段位：${medal.name}`}
      />
    </Tooltip>
  );
};
